import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  Users, Home, Calendar, DollarSign, TrendingUp, AlertTriangle, 
  Tag, BarChart3, Settings, ChevronRight, Check, X, Trash2,
  Eye, Edit, Ban, Mail, MapPin, Clock, CreditCard, Building,
  Search, Filter, Download, RefreshCw, Star, Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Navbar } from '@/components/Navbar';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Search and filter states
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [listingSearch, setListingSearch] = useState('');
  const [listingStatusFilter, setListingStatusFilter] = useState('all');
  const [bookingStatusFilter, setBookingStatusFilter] = useState('all');

  // New promo code form
  const [newPromo, setNewPromo] = useState({ code: '', discount_percent: 10, max_uses: 100 });
  
  // Selected item for detail view
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    // Wait for user to be loaded before checking role
    if (user === undefined) return;
    
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [statsRes, usersRes, listingsRes, bookingsRes, disputesRes, promoRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true, headers }),
        axios.get(`${API}/admin/users`, { withCredentials: true, headers }),
        axios.get(`${API}/admin/listings`, { withCredentials: true, headers }).catch(() => ({ data: { listings: [] } })),
        axios.get(`${API}/admin/bookings`, { withCredentials: true, headers }).catch(() => ({ data: { bookings: [] } })),
        axios.get(`${API}/admin/disputes`, { withCredentials: true, headers }),
        axios.get(`${API}/admin/promo`, { withCredentials: true, headers }),
        axios.get(`${API}/admin/pending-payouts`, { withCredentials: true, headers }).catch(() => ({ data: { payouts: [] } }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setListings(listingsRes.data.listings || listingsRes.data || []);
      setBookings(bookingsRes.data.bookings || bookingsRes.data || []);
      setDisputes(disputesRes.data.disputes || []);
      setPromoCodes(promoRes.data.promo_codes || []);
      setPendingPayouts(payoutsRes.data.payouts || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const createPromoCode = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/promo`, newPromo, { withCredentials: true, headers });
      toast.success('Promo code created');
      setNewPromo({ code: '', discount_percent: 10, max_uses: 100 });
      fetchData();
    } catch (error) {
      toast.error('Failed to create promo code');
    }
  };

  const deletePromoCode = async (code) => {
    if (!confirm(`Delete promo code "${code}"?`)) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/promo/${code}`, { withCredentials: true, headers });
      toast.success('Promo code deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true, headers });
      toast.success('User role updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const toggleUserBan = async (userId, currentlyBanned) => {
    const action = currentlyBanned ? 'unban' : 'ban';
    if (!confirm(`Are you sure you want to ${action} this user?`)) return;
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/users/${userId}/ban`, { banned: !currentlyBanned }, { withCredentials: true, headers });
      toast.success(`User ${action}ned`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} user`);
    }
  };

  const updateListingStatus = async (listingId, newStatus) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/listings/${listingId}/status`, { status: newStatus }, { withCredentials: true, headers });
      toast.success('Listing status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update listing status');
    }
  };

  const resolveDispute = async (disputeId, resolution) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/disputes/${disputeId}`, { 
        status: 'resolved', 
        resolution 
      }, { withCredentials: true, headers });
      toast.success('Dispute resolved');
      fetchData();
    } catch (error) {
      toast.error('Failed to resolve dispute');
    }
  };

  const markPayoutComplete = async (payoutId) => {
    if (!confirm('Mark this payout as completed? Make sure you have transferred the funds.')) return;
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/payouts/${payoutId}/complete`, {}, { withCredentials: true, headers });
      toast.success('Payout marked as complete');
      fetchData();
    } catch (error) {
      toast.error('Failed to update payout status');
    }
  };

  // Filter functions
  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(userSearch.toLowerCase()) || 
                          u.email?.toLowerCase().includes(userSearch.toLowerCase());
    const matchesRole = userRoleFilter === 'all' || u.role === userRoleFilter;
    return matchesSearch && matchesRole;
  });

  const filteredListings = listings.filter(l => {
    const matchesSearch = l.title?.toLowerCase().includes(listingSearch.toLowerCase()) || 
                          l.address?.toLowerCase().includes(listingSearch.toLowerCase());
    const matchesStatus = listingStatusFilter === 'all' || l.status === listingStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredBookings = bookings.filter(b => {
    return bookingStatusFilter === 'all' || b.status === bookingStatusFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5ebc67]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-600">Manage all SpotFinder data</p>
          </div>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <Users className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
              <p className="text-sm opacity-80">Total Users</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <Home className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-2xl font-bold">{stats?.total_listings || 0}</p>
              <p className="text-sm opacity-80">Listings</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <Calendar className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-2xl font-bold">{stats?.total_bookings || 0}</p>
              <p className="text-sm opacity-80">Bookings</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-4">
              <DollarSign className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-2xl font-bold">${(stats?.total_revenue || 0).toFixed(0)}</p>
              <p className="text-sm opacity-80">Revenue</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
            <CardContent className="p-4">
              <AlertTriangle className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-2xl font-bold">{disputes.filter(d => d.status === 'pending').length}</p>
              <p className="text-sm opacity-80">Open Disputes</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
            <CardContent className="p-4">
              <Building className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-2xl font-bold">{pendingPayouts.length}</p>
              <p className="text-sm opacity-80">Pending Payouts</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="overview" data-testid="admin-tab-overview">
              <BarChart3 className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">
              <Users className="w-4 h-4 mr-2" /> Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="listings" data-testid="admin-tab-listings">
              <Home className="w-4 h-4 mr-2" /> Listings ({listings.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">
              <Calendar className="w-4 h-4 mr-2" /> Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="payouts" data-testid="admin-tab-payouts">
              <DollarSign className="w-4 h-4 mr-2" /> Payouts
              {pendingPayouts.length > 0 && (
                <Badge variant="destructive" className="ml-2">{pendingPayouts.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="disputes" data-testid="admin-tab-disputes">
              <AlertTriangle className="w-4 h-4 mr-2" /> Disputes
            </TabsTrigger>
            <TabsTrigger value="promos" data-testid="admin-tab-promos">
              <Tag className="w-4 h-4 mr-2" /> Promos
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {users.slice(0, 5).map((u) => (
                      <div key={u.user_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{u.name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                        <Badge variant={u.role === 'host' ? 'default' : 'secondary'}>{u.role}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {bookings.slice(0, 5).map((b) => (
                      <div key={b.booking_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{b.listing_title || 'Booking'}</p>
                          <p className="text-sm text-slate-500">${b.total_amount?.toFixed(2)}</p>
                        </div>
                        <Badge variant={
                          b.status === 'completed' ? 'default' :
                          b.status === 'confirmed' ? 'secondary' :
                          b.status === 'cancelled' ? 'destructive' : 'outline'
                        }>{b.status}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>User Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span>Drivers</span>
                      <span className="font-bold">{users.filter(u => u.role === 'driver').length}</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full" 
                        style={{ width: `${(users.filter(u => u.role === 'driver').length / users.length * 100) || 0}%` }}
                      ></div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Hosts</span>
                      <span className="font-bold">{users.filter(u => u.role === 'host').length}</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${(users.filter(u => u.role === 'host').length / users.length * 100) || 0}%` }}
                      ></div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Admins</span>
                      <span className="font-bold">{users.filter(u => u.role === 'admin').length}</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-purple-500 h-2 rounded-full" 
                        style={{ width: `${(users.filter(u => u.role === 'admin').length / users.length * 100) || 0}%` }}
                      ></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Booking Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {bookings.filter(b => b.status === 'completed').length}
                      </p>
                      <p className="text-sm text-green-700">Completed</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {bookings.filter(b => b.status === 'confirmed').length}
                      </p>
                      <p className="text-sm text-blue-700">Confirmed</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-amber-600">
                        {bookings.filter(b => b.status === 'pending').length}
                      </p>
                      <p className="text-sm text-amber-700">Pending</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-600">
                        {bookings.filter(b => b.status === 'cancelled').length}
                      </p>
                      <p className="text-sm text-red-700">Cancelled</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>All Users ({filteredUsers.length})</span>
                </CardTitle>
                <div className="flex gap-4 mt-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by name or email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filter by role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="driver">Drivers</SelectItem>
                      <SelectItem value="host">Hosts</SelectItem>
                      <SelectItem value="admin">Admins</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium text-slate-600">User</th>
                        <th className="text-left p-3 font-medium text-slate-600">Email</th>
                        <th className="text-left p-3 font-medium text-slate-600">Phone</th>
                        <th className="text-left p-3 font-medium text-slate-600">Role</th>
                        <th className="text-left p-3 font-medium text-slate-600">Auth</th>
                        <th className="text-left p-3 font-medium text-slate-600">Joined</th>
                        <th className="text-left p-3 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <tr key={u.user_id} className="border-b hover:bg-slate-50">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {u.is_early_adopter && <Star className="w-4 h-4 text-amber-500" />}
                              <span className="font-medium">{u.name}</span>
                            </div>
                          </td>
                          <td className="p-3 text-slate-600">{u.email}</td>
                          <td className="p-3 text-slate-600">{u.phone || '-'}</td>
                          <td className="p-3">
                            <Select
                              value={u.role}
                              onValueChange={(value) => updateUserRole(u.user_id, value)}
                            >
                              <SelectTrigger className="w-28 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="driver">Driver</SelectItem>
                                <SelectItem value="host">Host</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline">
                              {u.auth_method || 'email'}
                            </Badge>
                          </td>
                          <td className="p-3 text-slate-600 text-sm">
                            {u.created_at ? format(new Date(u.created_at), 'MMM d, yyyy') : '-'}
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setSelectedUser(u)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant={u.banned ? 'default' : 'destructive'}
                                onClick={() => toggleUserBan(u.user_id, u.banned)}
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings">
            <Card>
              <CardHeader>
                <CardTitle>All Listings ({filteredListings.length})</CardTitle>
                <div className="flex gap-4 mt-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by title or address..."
                      value={listingSearch}
                      onChange={(e) => setListingSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={listingStatusFilter} onValueChange={setListingStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium text-slate-600">Listing</th>
                        <th className="text-left p-3 font-medium text-slate-600">Host</th>
                        <th className="text-left p-3 font-medium text-slate-600">Address</th>
                        <th className="text-left p-3 font-medium text-slate-600">Price</th>
                        <th className="text-left p-3 font-medium text-slate-600">Status</th>
                        <th className="text-left p-3 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredListings.map((l) => (
                        <tr key={l.listing_id} className="border-b hover:bg-slate-50">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              {l.photos?.[0] && (
                                <img src={l.photos[0]} alt="" className="w-12 h-12 rounded object-cover" />
                              )}
                              <div>
                                <p className="font-medium">{l.title}</p>
                                {l.is_priority_listing && (
                                  <Badge variant="secondary" className="text-xs">Priority</Badge>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-slate-600">{l.host_name || l.host_id}</td>
                          <td className="p-3 text-slate-600 text-sm max-w-xs truncate">{l.address}</td>
                          <td className="p-3 font-medium">${l.hourly_rate}/hr</td>
                          <td className="p-3">
                            <Select
                              value={l.status || 'draft'}
                              onValueChange={(value) => updateListingStatus(l.listing_id, value)}
                            >
                              <SelectTrigger className="w-32 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="published">Published</SelectItem>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="suspended">Suspended</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-3">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => window.open(`/listing/${l.listing_id}`, '_blank')}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card>
              <CardHeader>
                <CardTitle>All Bookings ({filteredBookings.length})</CardTitle>
                <div className="flex gap-4 mt-4">
                  <Select value={bookingStatusFilter} onValueChange={setBookingStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium text-slate-600">Booking ID</th>
                        <th className="text-left p-3 font-medium text-slate-600">Listing</th>
                        <th className="text-left p-3 font-medium text-slate-600">Driver</th>
                        <th className="text-left p-3 font-medium text-slate-600">Host</th>
                        <th className="text-left p-3 font-medium text-slate-600">Date/Time</th>
                        <th className="text-left p-3 font-medium text-slate-600">Amount</th>
                        <th className="text-left p-3 font-medium text-slate-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBookings.map((b) => (
                        <tr key={b.booking_id} className="border-b hover:bg-slate-50">
                          <td className="p-3 font-mono text-sm">{b.booking_id?.slice(0, 12)}...</td>
                          <td className="p-3">{b.listing_title || '-'}</td>
                          <td className="p-3 text-slate-600">{b.driver_name || b.driver_id}</td>
                          <td className="p-3 text-slate-600">{b.host_name || b.host_id}</td>
                          <td className="p-3 text-sm text-slate-600">
                            {b.start_time ? format(new Date(b.start_time), 'MMM d, h:mm a') : '-'}
                          </td>
                          <td className="p-3">
                            <div>
                              <p className="font-medium">${b.total_amount?.toFixed(2)}</p>
                              <p className="text-xs text-slate-500">Host: ${b.host_payout?.toFixed(2)}</p>
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge variant={
                              b.status === 'completed' ? 'default' :
                              b.status === 'confirmed' ? 'secondary' :
                              b.status === 'cancelled' ? 'destructive' : 'outline'
                            }>{b.status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Pending Bank Transfer Payouts ({pendingPayouts.length})
                </CardTitle>
                <CardDescription>
                  Manual bank transfer payouts that need to be processed
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingPayouts.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No pending payouts</p>
                ) : (
                  <div className="space-y-4">
                    {pendingPayouts.map((payout) => (
                      <div key={payout.payout_id} className="p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-slate-900">{payout.host_name}</p>
                              <Badge variant="outline">{payout.host_email}</Badge>
                            </div>
                            <div className="mt-2 p-3 bg-white rounded border">
                              <p className="text-xs text-slate-500 mb-1">Bank Details</p>
                              <p className="font-mono text-sm">
                                <span className="text-slate-600">BSB:</span> {payout.bsb_masked}
                              </p>
                              <p className="font-mono text-sm">
                                <span className="text-slate-600">Account:</span> {payout.account_masked}
                              </p>
                              <p className="font-mono text-sm">
                                <span className="text-slate-600">Name:</span> {payout.account_name}
                              </p>
                            </div>
                            <div className="mt-2 text-sm text-slate-600">
                              <p>Booking: {payout.booking_id}</p>
                              <p>Completed: {payout.completed_at ? format(new Date(payout.completed_at), 'PPp') : 'N/A'}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-600">${payout.amount?.toFixed(2)}</p>
                            <Badge variant={payout.status === 'pending' ? 'destructive' : 'secondary'}>
                              {payout.status}
                            </Badge>
                          </div>
                        </div>
                        {payout.status === 'pending' && (
                          <div className="flex gap-2 mt-4 pt-4 border-t">
                            <Button 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => markPayoutComplete(payout.payout_id)}
                            >
                              <Check className="w-4 h-4 mr-2" />
                              Mark as Paid
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Disputes Tab */}
          <TabsContent value="disputes">
            <Card>
              <CardHeader>
                <CardTitle>Disputes ({disputes.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {disputes.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No disputes</p>
                ) : (
                  <div className="space-y-4">
                    {disputes.map((d) => (
                      <div key={d.dispute_id} className="p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold">{d.subject || 'Dispute'}</p>
                            <p className="text-sm text-slate-600">{d.description}</p>
                          </div>
                          <Badge variant={d.status === 'resolved' ? 'default' : 'destructive'}>
                            {d.status}
                          </Badge>
                        </div>
                        {d.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => resolveDispute(d.dispute_id, 'Resolved by admin')}
                            >
                              Resolve
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promos">
            <Card>
              <CardHeader>
                <CardTitle>Promo Codes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-medium mb-3">Create New Promo Code</h3>
                  <div className="flex gap-3 flex-wrap">
                    <Input
                      placeholder="Code (e.g., SUMMER20)"
                      value={newPromo.code}
                      onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                      className="flex-1 min-w-[150px]"
                    />
                    <Input
                      type="number"
                      placeholder="Discount %"
                      value={newPromo.discount_percent}
                      onChange={(e) => setNewPromo({ ...newPromo, discount_percent: parseInt(e.target.value) || 0 })}
                      className="w-28"
                    />
                    <Input
                      type="number"
                      placeholder="Max Uses"
                      value={newPromo.max_uses}
                      onChange={(e) => setNewPromo({ ...newPromo, max_uses: parseInt(e.target.value) || 0 })}
                      className="w-28"
                    />
                    <Button onClick={createPromoCode} className="bg-[#5ebc67] hover:bg-[#4da855]">
                      Create
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  {promoCodes.map((p) => (
                    <div key={p.code} className="flex items-center justify-between p-4 bg-white border rounded-lg">
                      <div>
                        <p className="font-mono font-bold text-lg">{p.code}</p>
                        <p className="text-sm text-slate-600">
                          {p.discount_percent}% off • {p.current_uses || 0}/{p.max_uses} uses
                        </p>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deletePromoCode(p.code)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {promoCodes.length === 0 && (
                    <p className="text-slate-500 text-center py-4">No promo codes yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* User Detail Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-xl font-bold">User Details</h2>
                <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  {selectedUser.picture ? (
                    <img src={selectedUser.picture} alt="" className="w-16 h-16 rounded-full" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center">
                      <Users className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-xl font-semibold">{selectedUser.name}</p>
                    <Badge>{selectedUser.role}</Badge>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Email</p>
                    <p className="font-medium">{selectedUser.email}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Phone</p>
                    <p className="font-medium">{selectedUser.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">User ID</p>
                    <p className="font-mono text-xs">{selectedUser.user_id}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Auth Method</p>
                    <p className="font-medium">{selectedUser.auth_method || 'email'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Joined</p>
                    <p className="font-medium">
                      {selectedUser.created_at ? format(new Date(selectedUser.created_at), 'PPP') : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Early Adopter</p>
                    <p className="font-medium">{selectedUser.is_early_adopter ? 'Yes ⭐' : 'No'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
